import { useEffect, useState } from "react";
import {
  getAuth,
  OAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
} from "firebase/auth";
import { ref, set, get } from "firebase/database"; // Firebase Realtime Database methods
import { db } from "../firebase"; // Adjust the import to your Firebase setup
import Notification from "../Components/Notification.js";
import { useTheme } from "@mui/material";

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [signInError, setSignInError] = useState(false);
  const [isCheckingUser, setIsCheckingUser] = useState(true); // Loading state for checking user
  const [isSigningIn, setIsSigningIn] = useState(false); // Prevent multiple sign-ins
  const theme = useTheme();
  const auth = getAuth();

  // Initiate sign-in process using popup method
  const signIn = async () => {
    if (isSigningIn || user) return; // Prevent multiple popups
    setIsSigningIn(true); // Set signing in state

    const provider = new OAuthProvider("microsoft.com");
    provider.setCustomParameters({
      tenant: "d23819f6-19cb-40b9-aef1-8a42e28d3cd7", // Adjust for your tenant ID
    });

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      setUser(user); // Set user on successful sign-in
      setSignInError(false);
      await checkIfUserExists(user); // Check if user exists in the database
    } catch (error) {
      console.error("Error during sign-in:", error);
      setSignInError(true); // Set error on sign-in failure
      Notification.error(theme, "Error", "Sign-in failed. Please try again.");
    } finally {
      setIsSigningIn(false); // Reset sign-in state after attempt
    }
  };

  // Check if user exists in Firebase database
  const checkIfUserExists = async (user) => {
    try {
      const userRef = ref(db, `users/${user.uid}`);
      const snapshot = await get(userRef);
      if (!snapshot.exists()) {
        // If user does not exist, prompt for identifier
        await promptForIdentifier(user);
      } else {
        setIsCheckingUser(false); // Stop loading when user is found
      }
    } catch (error) {
      console.error("Error checking user existence:", error);
      Notification.error(theme, "Error", "Error checking user status.");
    }
  };

  // Prompt for identifier when new user signs in
  const promptForIdentifier = async (user) => {
    const identifier = await Notification.promptForName(theme);
    if (identifier) {
      await createUserInDatabase(user, identifier);
    } else {
      Notification.error(theme, "Error", "You must enter a valid name.");
    }
  };

  // Create a new user in the database
  const createUserInDatabase = async (user, identifier) => {
    const userRef = ref(db, `users/${user.uid}`);
    try {
      await set(userRef, {
        Email: user.email,
        Identifier: identifier,
        isAdmin: false,
      });
      Notification.success(theme, "Welcome!", "Your profile has been created.");
      setIsCheckingUser(false);
    } catch (error) {
      console.error("Error creating user in database:", error);
      Notification.error(theme, "Error", "Error creating your profile.");
    }
  };

  // Check auth state on component mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user); // Set user if logged in
        setSignInError(false); // Clear error state
        await checkIfUserExists(user); // Check if user exists
      } else {
        setUser(null); // Clear user state
        setIsCheckingUser(false);
      }
    });

    return () => unsubscribe(); // Cleanup on component unmount
  }, );

  return { user, signInError, isCheckingUser, signIn };
};

export default useAuth;
